import { planogramPerformanceRecordValidation } from "@CommonTypes/psa/PlanogramPerformanceRecord";
import { fixtureRecordValidation } from "@CommonTypes/psa/FixtureRecord";
import { planogramRecordValidation } from "@CommonTypes/psa/PlanogramRecord";
import { type PositionRecord, positionRecordValidation } from "@CommonTypes/psa/PositionRecord";
import { type ProductRecord, productRecordValidation } from "@CommonTypes/psa/ProductRecord";
import {
  getPsaSqueezeFlag,
  getPsaSqueezeSizeMode,
  type PsaSqueezeFlagInput,
  type PsaSqueezeInput,
} from "./PsaSqueeze";

const splitEscapedFields = (value: string) => {
  const fields: string[] = [];
  let currentField = "";

  for (let index = 0; index < value.length; index += 1) {
    const char = value[index];
    const nextChar = value[index + 1];

    if (char === "\\" && nextChar === "\\") {
      currentField += "\\";
      index += 1;
      continue;
    }

    if (char === "\\" && nextChar === ",") {
      currentField += ",";
      index += 1;
      continue;
    }

    if (char === ",") {
      fields.push(currentField);
      currentField = "";
      continue;
    }

    currentField += char;
  }

  fields.push(currentField);
  return fields;
};

const normalizeFieldValue = (value: string | number | null | undefined) => {
  if (value == null) {
    return "";
  }

  return String(value)
    .replaceAll("\\", "\\\\")
    .replaceAll(",", "\\,")
    .replaceAll("\n", " ")
    .replaceAll("\r", " ");
};

export const splitPsaRecordLine = (rawRecord: string) => {
  const [recordType, ...fields] = splitEscapedFields(rawRecord);

  if (!recordType) {
    throw new Error("Invalid PSA record line: missing record type.");
  }

  return { recordType, fields };
};

export const serializePsaRecordLine = (
  recordType: string,
  fields: (string | number | null | undefined)[],
) => [recordType, ...fields.map((field) => normalizeFieldValue(field))].join(",");

const updatePsaRecordFields = (
  rawRecord: string,
  fieldOrder: string[],
  updates: Record<string, string | number | null | undefined>,
) => {
  const { recordType, fields } = splitPsaRecordLine(rawRecord);

  for (const [fieldName, fieldValue] of Object.entries(updates)) {
    const fieldIndex = fieldOrder.indexOf(fieldName);
    if (fieldIndex < 0) {
      continue;
    }

    while (fields.length <= fieldIndex) {
      fields.push("");
    }

    fields[fieldIndex] = fieldValue == null ? "" : String(fieldValue);
  }

  return serializePsaRecordLine(recordType, fields);
};

const POSITION_FIELD_ORDER = Object.keys(positionRecordValidation);
const PERFORMANCE_FIELD_ORDER = Object.keys(planogramPerformanceRecordValidation);
const FIXTURE_FIELD_ORDER = Object.keys(fixtureRecordValidation);
const PRODUCT_FIELD_ORDER = Object.keys(productRecordValidation);
const PLANOGRAM_FIELD_ORDER = Object.keys(planogramRecordValidation);

export const buildPositionSqueezeFieldUpdates = (
  squeezeInput: PsaSqueezeFlagInput,
): Pick<
  PositionRecord,
  "merchXSize" | "merchYSize" | "merchZSize" | "merchXSqueeze" | "merchYSqueeze" | "merchZSqueeze"
> => ({
  merchXSize: getPsaSqueezeSizeMode(squeezeInput.squeezeWidth),
  merchYSize: getPsaSqueezeSizeMode(squeezeInput.squeezeHeight),
  merchZSize: getPsaSqueezeSizeMode(squeezeInput.squeezeDepth),
  merchXSqueeze: getPsaSqueezeFlag(squeezeInput.squeezeWidth),
  merchYSqueeze: getPsaSqueezeFlag(squeezeInput.squeezeHeight),
  merchZSqueeze: getPsaSqueezeFlag(squeezeInput.squeezeDepth),
});

export const buildProductSqueezeFieldUpdates = (
  squeezeInput: PsaSqueezeInput,
): Pick<
  ProductRecord,
  | "merchXSqueeze"
  | "merchYSqueeze"
  | "merchZSqueeze"
  | "minimumSqueezeFactorX"
  | "minimumSqueezeFactorY"
  | "minimumSqueezeFactorZ"
> => ({
  merchXSqueeze: getPsaSqueezeFlag(squeezeInput.squeezeWidth),
  merchYSqueeze: getPsaSqueezeFlag(squeezeInput.squeezeHeight),
  merchZSqueeze: getPsaSqueezeFlag(squeezeInput.squeezeDepth),
  minimumSqueezeFactorX: squeezeInput.squeezeWidthPct,
  minimumSqueezeFactorY: squeezeInput.squeezeHeightPct,
  minimumSqueezeFactorZ: squeezeInput.squeezeDepthPct,
});

const PRODUCT_SQUEEZE_FIELDS_TO_REFRESH: (keyof ProductRecord)[] = [
  "merchXSqueeze",
  "merchYSqueeze",
  "merchZSqueeze",
  "minimumSqueezeFactorX",
  "minimumSqueezeFactorY",
  "minimumSqueezeFactorZ",
];

const POSITION_FIELDS_TO_REFRESH: (keyof PositionRecord)[] = [
  "x",
  "width",
  "y",
  "height",
  "z",
  "depth",
  "merchStyle",
  "hFacings",
  "vFacings",
  "dFacings",
  "xCapNum",
  "xCapNested",
  "xCapReversed",
  "xCapOrientation",
  "yCapNum",
  "yCapNested",
  "yCapReversed",
  "yCapOrientation",
  "zCapNum",
  "zCapNested",
  "zCapReversed",
  "zCapOrientation",
  "orientation",
  "merchXSize",
  "merchYSize",
  "merchZSize",
  "merchXSqueeze",
  "merchYSqueeze",
  "merchZSqueeze",
];

const buildPositionFieldUpdates = (positionRecord: PositionRecord) => ({
  ...Object.fromEntries(
    POSITION_FIELDS_TO_REFRESH.map((fieldName) => [fieldName, positionRecord[fieldName]]),
  ),
});

// Most callers only refresh the standard geometry/orientation/squeeze fields above.
// `additionalUpdates` exists as an explicit escape hatch for serializers that
// need to override extra PSA columns carried in the source raw record. Pegboard
// hole coordinates are not PSA Position fields; they remain derived from the
// exported x/y geometry plus the pegboard fixture metadata.
export const refreshPositionRawRecord = (
  originalRawRecord: string,
  generatedPositionRecord: PositionRecord,
  additionalUpdates?: Record<string, string | number | null | undefined>,
) =>
  updatePsaRecordFields(originalRawRecord, POSITION_FIELD_ORDER, {
    ...buildPositionFieldUpdates(generatedPositionRecord),
    ...additionalUpdates,
  });

export const createPositionRawRecord = (
  templateRawRecord: string,
  generatedPositionRecord: PositionRecord,
  additionalUpdates?: Record<string, string | number | null | undefined>,
) =>
  updatePsaRecordFields(templateRawRecord, POSITION_FIELD_ORDER, {
    ...buildPositionFieldUpdates(generatedPositionRecord),
    ...additionalUpdates,
  });

export const refreshPositionSqueezeRawRecord = (
  originalRawRecord: string,
  squeezeInput: PsaSqueezeFlagInput,
) =>
  updatePsaRecordFields(
    originalRawRecord,
    POSITION_FIELD_ORDER,
    buildPositionSqueezeFieldUpdates(squeezeInput),
  );

export const refreshProductSqueezeRawRecord = (
  originalRawRecord: string,
  squeezeInput: PsaSqueezeInput,
) =>
  updatePsaRecordFields(
    originalRawRecord,
    PRODUCT_FIELD_ORDER,
    buildProductSqueezeFieldUpdates(squeezeInput),
  );

export interface PerformanceTemplateInput {
  upc: string;
  productId: string | number;
  price: number;
  sales: number;
  profit: number;
  unitCost: number;
  unitMovement: number;
  facings: number;
  units: number;
  capacity: number;
  linear: number;
  square: number;
  cubic: number;
  numberOfPositions: number;
  recommendedFacings: number;
}

export const createPerformanceRawRecord = (
  templateRawRecord: string,
  input: PerformanceTemplateInput,
) =>
  updatePsaRecordFields(templateRawRecord, PERFORMANCE_FIELD_ORDER, {
    upc: input.upc,
    dbParentProductKey: input.productId,
    price: input.price,
    unitMovement: input.unitMovement,
    facings: input.facings,
    units: input.units,
    capacity: input.capacity,
    linear: input.linear,
    square: input.square,
    cubic: input.cubic,
    sales: input.sales,
    unitCost: input.unitCost,
    unitProfit: input.profit,
    profit: input.profit,
    salesLinear: input.linear === 0 ? 0 : input.sales / input.linear,
    salesSquare: input.square === 0 ? 0 : input.sales / input.square,
    salesCubic: input.cubic === 0 ? 0 : input.sales / input.cubic,
    profitLinear: input.linear === 0 ? 0 : input.profit / input.linear,
    profitSquare: input.square === 0 ? 0 : input.profit / input.square,
    profitCubic: input.cubic === 0 ? 0 : input.profit / input.cubic,
    numberOfPositions: input.numberOfPositions,
    recommendedFacings: input.recommendedFacings,
    combinedPerformanceIndex: 0,
  });

export const createFixtureRawRecord = (
  templateRawRecord: string,
  updates: Record<string, string | number | null | undefined>,
) => updatePsaRecordFields(templateRawRecord, FIXTURE_FIELD_ORDER, updates);

export const refreshPlanogramRawRecord = (
  rawRecord: string,
  updates: Record<string, string | number | null | undefined>,
) => updatePsaRecordFields(rawRecord, PLANOGRAM_FIELD_ORDER, updates);

export const refreshProductRawRecord = (
  originalRawRecord: string,
  generatedProductRawRecord: string,
): string => {
  const { fields: generatedFields } = splitPsaRecordLine(generatedProductRawRecord);
  const updates = Object.fromEntries(
    PRODUCT_SQUEEZE_FIELDS_TO_REFRESH.map((field) => {
      const index = PRODUCT_FIELD_ORDER.indexOf(field);
      return [field, generatedFields[index] ?? ""];
    }),
  );
  return updatePsaRecordFields(originalRawRecord, PRODUCT_FIELD_ORDER, updates);
};
